import type {
  ContentPostTableRow,
  MetricCellData,
} from '../types/contentTableRow';
import type { MetricColumnDef } from './contentTableColumns';

function columnMaxMin(
  rows: ContentPostTableRow[],
  key: MetricColumnDef['key'],
): { max: number; min: number } | null {
  const raws: number[] = [];
  for (const row of rows) {
    const raw = row.metrics[key]?.raw;
    if (raw !== undefined && Number.isFinite(raw)) {
      raws.push(raw);
    }
  }
  const distinct = new Set(raws);
  if (distinct.size < 2) {
    return null;
  }
  const max = Math.max(...raws);
  const min = Math.min(...raws);
  if (max === min) {
    return null;
  }
  return { max, min };
}

/**
 * Sets per-column max (green) / min (red) highlights across loaded rows for the
 * visible metric columns only.
 */
export function applyContentTableHighlights(
  rows: ContentPostTableRow[],
  columns: MetricColumnDef[],
): ContentPostTableRow[] {
  if (rows.length === 0) {
    return rows;
  }

  const statsByKey = new Map<
    MetricColumnDef['key'],
    { max: number; min: number } | null
  >();
  for (const col of columns) {
    statsByKey.set(col.key, columnMaxMin(rows, col.key));
  }

  return rows.map((row) => {
    const metrics = { ...row.metrics };

    for (const col of columns) {
      const key = col.key;
      const cell = row.metrics[key];
      const stats = statsByKey.get(key);

      if (!stats || cell?.raw === undefined || !Number.isFinite(cell.raw)) {
        const next: MetricCellData = { ...cell, highlight: undefined };
        metrics[key] = next;
        continue;
      }

      let highlight: MetricCellData['highlight'];
      if (cell.raw === stats.max) {
        highlight = 'positive';
      } else if (cell.raw === stats.min) {
        highlight = 'negative';
      }

      metrics[key] = { ...cell, highlight };
    }

    return { ...row, metrics };
  });
}
